import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Bot, Clock, History, RefreshCw, User } from 'lucide-react';
import { useUserLookup } from '../../hooks/useUserLookup';
import { projectDataClient } from '../../lib/connect/clients';
/**
 * EntityAuditTrail — reusable component that shows the change history
 * for a specific entity (requirement, CLIN, volume, etc.).
 *
 * Drop this into any detail drawer/panel:
 *
 *   <EntityAuditTrail
 *     projectId="proj_123"
 *     resourceType="requirement"
 *     resourceId="req_456"
 *   />
 *
 * Queries the activity feed filtered by resource type + ID, showing
 * who changed what and when — with before/after diffs inline.
 */ export function EntityAuditTrail({ projectId, resourceType, resourceId }) {
    const userLookup = useUserLookup(projectId);
    const { data, isLoading } = useQuery({
        queryKey: [
            'entityAudit',
            projectId,
            resourceType,
            resourceId
        ],
        queryFn: async ()=>{
            const response = await projectDataClient.listProjectActivity({
                projectId,
                pagination: {
                    pageSize: 50,
                    pageToken: ''
                }
            });
            // Client-side filter to this entity. The API doesn't support
            // resource_type/resource_id filters yet — filter in memory.
            const filtered = (response.entries ?? []).filter((e)=>e.resourceType === resourceType && e.resourceId === resourceId);
            return filtered;
        },
        staleTime: 15000,
        enabled: !!projectId && !!resourceId
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "py-4 text-center",
            children: /*#__PURE__*/ _jsx("div", {
                className: "inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500"
            })
        });
    }
    const entries = data ?? [];
    if (entries.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 rounded-lg border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500",
            children: [
                /*#__PURE__*/ _jsx(History, {
                    className: "h-3.5 w-3.5 text-slate-400"
                }),
                "No changes recorded for this item."
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400",
                children: [
                    /*#__PURE__*/ _jsx(History, {
                        className: "h-3 w-3"
                    }),
                    "Change history"
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative border-l-2 border-slate-200 pl-4",
                children: entries.map((entry, i)=>{
                    const actorType = entry.actorType || 'user';
                    const ActorIcon = ACTOR_ICONS[actorType] || User;
                    const actorColor = ACTOR_COLORS[actorType] || 'text-slate-400 bg-slate-100';
                    const userName = entry.actorUserId ? userLookup.get(entry.actorUserId) || entry.actorUserId.slice(0, 8) : actorType === 'system' ? 'System' : 'AI';
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "relative pb-4 last:pb-0",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: `absolute -left-[21px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full ${actorColor}`,
                                children: /*#__PURE__*/ _jsx(ActorIcon, {
                                    className: "h-2.5 w-2.5"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-[12px]",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-semibold text-slate-800",
                                                children: userName
                                            }),
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-slate-600",
                                                children: entry.summary
                                            })
                                        ]
                                    }),
                                    entry.detailsJson && /*#__PURE__*/ _jsx(ChangeDetails, {
                                        detailsJson: entry.detailsJson
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-0.5 flex items-center gap-1 text-[10px] text-slate-400",
                                        children: [
                                            /*#__PURE__*/ _jsx(Clock, {
                                                className: "h-2.5 w-2.5"
                                            }),
                                            formatRelative(entry.at?.toDate())
                                        ]
                                    })
                                ]
                            })
                        ]
                    }, entry.id || i);
                })
            })
        ]
    });
}
function ChangeDetails({ detailsJson }) {
    if (!detailsJson) return null;
    try {
        const data = JSON.parse(detailsJson);
        if (data.previous !== undefined && data.new !== undefined) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "mt-1 flex items-center gap-1.5 text-[11px]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through",
                        children: String(data.previous || '(empty)')
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-slate-400",
                        children: "→"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700",
                        children: String(data.new)
                    })
                ]
            });
        }
        if (data.previousStatus !== undefined && data.newStatus !== undefined) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "mt-1 flex items-center gap-1.5 text-[11px]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through",
                        children: data.previousStatus || '(none)'
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-slate-400",
                        children: "→"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700",
                        children: data.newStatus
                    })
                ]
            });
        }
        const diffEntries = Object.entries(data).filter(([, v])=>typeof v === 'object' && v !== null && 'from' in v && 'to' in v);
        if (diffEntries.length > 0) {
            return /*#__PURE__*/ _jsx("div", {
                className: "mt-1 space-y-0.5",
                children: diffEntries.map(([field, diff])=>{
                    const d = diff;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 text-[11px]",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-medium text-slate-500",
                                children: [
                                    field,
                                    ":"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through",
                                children: d.from || '(empty)'
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-slate-400",
                                children: "→"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700",
                                children: d.to
                            })
                        ]
                    }, field);
                })
            });
        }
    } catch  {
    /* skip */ }
    return null;
}
function formatRelative(date) {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
}
const ACTOR_ICONS = {
    user: User,
    system: RefreshCw,
    ai: Bot
};
const ACTOR_COLORS = {
    user: 'text-blue-600 bg-blue-50',
    system: 'text-slate-500 bg-slate-100',
    ai: 'text-purple-600 bg-purple-50'
};
